import {
  Component,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import { CompanyDetails } from '../../models/company-details';
import {
  Connection,
  MatchedRecordConnections,
  MatchedRecordDetails,
} from '../../../pages/outreach/models/connection.model';
import { BUSINESS_SUPPORT_KEY_TO_DISPLAY_NAME } from '../../../pages/outreach/models/leads-criteria.model';
import { LoggedInUser } from '../../models/logged-in-user';
import { AuthService } from '../../../pages/auth/services/auth.service';
import { SearchService } from '../../../pages/search/services/search.service';
import { PopupComponent } from '../popup/popup.component';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { CommonService } from '../../services/common/common.service';
import { ToastType } from '../../interfaces/common';
import { PermissionCode } from '../../enums/permission-code';

@Component({
  selector: 'app-record-matched-cdfis',
  standalone: true,
  imports: [DecimalPipe, PopupComponent, FormsModule],
  templateUrl: './record-matched-cdfis.component.html',
  styleUrl: './record-matched-cdfis.component.scss',
})
export class RecordMatchedCDFIsComponent
  extends DestroyBase
  implements OnChanges, AfterViewChecked
{
  @Input() recordDetails!: CompanyDetails;
  @ViewChild('criteriaTabsContainer')
  criteriaTabsContainer?: ElementRef<HTMLDivElement>;
  matchedConnections: MatchedRecordConnections[] = [];

  showLeftArrow = false;
  showRightArrow = false;

  authService = inject(AuthService);
  private searchService = inject(SearchService);
  private commonService = inject(CommonService);
  loggedInUserInfo: LoggedInUser | null = null;
  showDetailsPopup = false;
  showFullDetails = false;
  matchedRecordDetails: MatchedRecordConnections | null = null;
  selectedCriteriaDetails: MatchedRecordDetails | null = null;
  hasLoanAmount = false;
  matchedConnectionsWithLoanAmount: Connection[] | null = null;
  selectedConnectionForSharing: Connection[] | null = null;
  showConnectionsWithLoanAmount = false;

  readonly isAdmin = this.authService.hasPermission(
    PermissionCode.ADMIN_PRIVILEGES,
  );

  ngAfterViewChecked(): void {
    this.updateScrollArrows();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['recordDetails'] && changes['recordDetails'].currentValue) {
      this.loggedInUserInfo = this.authService.getLoggedInUserInfo();
      this.loadMatchedConnections();
    }
  }

  loadMatchedConnections(): void {
    this.matchedConnections =
      this.recordDetails?.matched_record_connections || [];
    this.hasLoanAmount = !!this.recordDetails?.detail?.properties?.find(
      (prop) => prop.property === 'loan_amount' && prop.value,
    );
    if (this.hasLoanAmount) {
      this.searchService
        .getMatchedConnectionsWithLoanAmount(this.recordDetails.unique_id)
        .pipe(takeUntil(this.destroy$))
        .subscribe((connections) => {
          this.matchedConnectionsWithLoanAmount = connections.filter(
            (con) => con.connection_name !== this.loggedInUserInfo?.connection,
          );
          this.selectedConnectionForSharing = [];
        });
    }
  }

  isCurrentUserConnection(cdfi: Connection): boolean {
    return (
      cdfi.connection_name === this.loggedInUserInfo?.connection ||
      cdfi.connection_name === this.loggedInUserInfo?.username
    );
  }

  onMatchedRecordDetails(cdfi: MatchedRecordConnections): void {
    const sortedCriterias = [...cdfi.matched_criterias].sort(
      (a, b) => b.match_percentage - a.match_percentage,
    );

    this.matchedRecordDetails = {
      ...cdfi,
      matched_criterias: sortedCriterias,
    };
    this.selectedCriteriaDetails = sortedCriterias[0] || null;
    this.showFullDetails = this.isAdmin || this.isCurrentUserConnection(cdfi);
    this.showDetailsPopup = true;
  }

  onSelectedCriteria(criteria: MatchedRecordDetails): void {
    this.selectedCriteriaDetails = criteria;
  }

  onConnectionSelected(isChecked: boolean, connection: Connection) {
    if (isChecked) {
      this.selectedConnectionForSharing = [
        ...(this.selectedConnectionForSharing || []),
        connection,
      ];
    } else {
      this.selectedConnectionForSharing =
        this.selectedConnectionForSharing?.filter(
          (conn) => conn.connection_name !== connection.connection_name,
        ) || null;
    }
  }

  onShareSelected(): void {
    if (!this.selectedConnectionForSharing?.length) return;

    const shareRecord = {
      connections: this.selectedConnectionForSharing.map(
        (c) => c.connection_name,
      ),
      users: [],
    };

    this.searchService
      .shareRecord(shareRecord, this.recordDetails.unique_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Record shared successfully with selected connections',
          });
          this.showConnectionsWithLoanAmount = false;
          this.selectedConnectionForSharing = null;
        },
        error: () => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Failed to share record.',
          });
        },
      });
  }

  onCancelShareConnectionsPopup() {
    this.selectedConnectionForSharing = [];
    this.showConnectionsWithLoanAmount = false;
  }

  getDistinctProducts(connection: MatchedRecordConnections): string[] {
    return this.getUniqueValues(
      connection.matched_criterias.flatMap(
        (criteria) => criteria.matched_criteria?.products || [],
      ),
    ).map((product) => this.getProductDisplayName(product));
  }

  getBestMatchPercentage(connection: MatchedRecordConnections): number {
    if (!connection.matched_criterias.length) return 0;

    return Math.max(
      ...connection.matched_criterias.map(
        (criteria) => criteria.match_percentage,
      ),
    );
  }

  getSourceLabel(source: string): string {
    return source === 'custom' ? 'Custom' : 'Deeproots';
  }

  private getUniqueValues(values: string[]): string[] {
    return [...new Set(values.filter(Boolean))];
  }

  getMappedProductNames(products: string[]): string[] {
    return products.map((product) => this.getProductDisplayName(product));
  }

  private getProductDisplayName(productKey: string): string {
    return BUSINESS_SUPPORT_KEY_TO_DISPLAY_NAME[productKey] || productKey;
  }

  scrollTabs(direction: 'left' | 'right'): void {
    if (!this.criteriaTabsContainer) return;

    const container = this.criteriaTabsContainer.nativeElement;
    const scrollAmount = 300;
    const targetScroll =
      direction === 'left'
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });

    // Update arrows after scroll animation completes
    setTimeout(() => this.updateScrollArrows(), 300);
  }

  updateScrollArrows(): void {
    if (!this.criteriaTabsContainer) return;

    const container = this.criteriaTabsContainer.nativeElement;
    const hasOverflow = container.scrollWidth > container.clientWidth;

    this.showLeftArrow = hasOverflow && container.scrollLeft > 0;
    this.showRightArrow =
      hasOverflow &&
      container.scrollLeft < container.scrollWidth - container.clientWidth - 1;
  }
}
